import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { getDramaById, getReviewsByDrama, getRatingAverage } from "@/lib/content";
import type { Review } from "@/mocks/reviews";
import Stars from "@/components/base/Stars";
import Breadcrumb from "@/components/feature/Breadcrumb";

const filterOptions = [
  { value: "all", label: "全部评分" },
  { value: "5", label: "5 星" },
  { value: "4", label: "4 星" },
  { value: "3", label: "3 星" },
  { value: "2", label: "2 星" },
  { value: "1", label: "1 星" },
];

const storageKey = (dramaId: string) => `fengliu_reviews_${dramaId}`;

const loadLocalReviews = (dramaId: string): Review[] => {
  try {
    const raw = localStorage.getItem(storageKey(dramaId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export default function Reviews() {
  const { id } = useParams();
  const drama = getDramaById(id);

  const [ratingFilter, setRatingFilter] = useState("all");
  const [localReviews, setLocalReviews] = useState<Review[]>(() =>
    id ? loadLocalReviews(id) : []
  );
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [content, setContent] = useState("");
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");

  useSeo({
    title: drama
      ? `风流女管家 - ${drama.title}剧集点评与观众评分`
      : "风流女管家 - 剧集点评",
    description: drama
      ? `${drama.title}观众点评与评分汇总，查看真实观剧感受与评价，并留下你的看法。`
      : "风流女管家剧集点评页，查看观众评分与点评内容。",
    keywords: drama
      ? `${drama.title}点评,${drama.title}评价,剧集点评,风流女管家`
      : "风流女管家,剧集点评,观众评分",
  });

  const allReviews = useMemo(() => {
    if (!drama) return [];
    const base = getReviewsByDrama(drama.id);
    return [...localReviews, ...base].sort((a, b) =>
      a.date < b.date ? 1 : -1
    );
  }, [drama, localReviews]);

  const visibleReviews = useMemo(
    () =>
      ratingFilter === "all"
        ? allReviews
        : allReviews.filter((r) => String(r.rating) === ratingFilter),
    [allReviews, ratingFilter]
  );

  const avg = getRatingAverage(allReviews);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFeedback("");
    setError("");
    const trimmedName = name.trim();
    const trimmedContent = content.trim();
    if (!trimmedName) {
      setError("请填写你的昵称");
      return;
    }
    if (!trimmedContent) {
      setError("请填写点评内容");
      return;
    }
    if (trimmedContent.length > 500) {
      setError("点评内容不得超过 500 字");
      return;
    }
    if (!drama) return;

    const newReview: Review = {
      id: `local-${Date.now()}`,
      dramaId: drama.id,
      user: trimmedName,
      rating,
      content: trimmedContent,
      date: new Date().toISOString().slice(0, 10),
      likes: 0,
    };
    const updated = [newReview, ...localReviews];
    setLocalReviews(updated);
    try {
      localStorage.setItem(storageKey(drama.id), JSON.stringify(updated));
    } catch {
      // ignore storage errors
    }
    setName("");
    setRating(5);
    setContent("");
    setFeedback("点评已提交，已显示在下方的点评列表中。");
  };

  if (!drama) {
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
          <i className="ri-error-warning-line text-3xl" />
        </span>
        <h1 className="font-heading text-2xl font-bold text-foreground-950">
          该剧集不存在或已下架
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          无法查看点评内容，去看看其他剧集吧。
        </p>
        <Link
          to="/category"
          className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line" />
          返回剧集分类
        </Link>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-6xl px-4 py-6 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "剧集点评", to: `/reviews/${drama.id}` },
              { label: drama.title },
            ]}
          />
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6">
        {/* Header */}
        <section className="flex flex-col gap-5 sm:flex-row sm:items-center">
          <span className="block h-32 w-24 shrink-0 overflow-hidden rounded-lg bg-background-200">
            <img
              src={drama.poster}
              alt={`${drama.title}海报`}
              title={`${drama.title} 剧集海报`}
              className="h-full w-full object-cover object-top"
            />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-primary-600">剧集点评</p>
            <h1 className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {drama.title}
            </h1>
            <p className="mt-1 text-sm text-foreground-500">
              {drama.year} · {drama.genre} · 全 {drama.episodes} 集
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-4">
              <span className="flex items-baseline gap-1">
                <span className="font-heading text-3xl font-bold text-primary-600">
                  {avg}
                </span>
                <span className="text-sm text-foreground-400">/ 10</span>
              </span>
              <span>
                <Stars value={avg} className="text-base" />
                <span className="mt-0.5 block text-xs text-foreground-500">
                  {allReviews.length} 条点评
                </span>
              </span>
              <Link
                to={`/detail/${drama.id}`}
                className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
              >
                查看剧集详情
                <i className="ri-arrow-right-s-line" />
              </Link>
            </div>
          </div>
        </section>

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px]">
          {/* Review list */}
          <div className="min-w-0">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {filterOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setRatingFilter(opt.value)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                    ratingFilter === opt.value
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {visibleReviews.length > 0 ? (
              <div className="space-y-3">
                {visibleReviews.map((review) => (
                  <div
                    key={review.id}
                    className="rounded-lg border border-background-200 bg-background-50 p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-sm font-medium text-primary-700">
                          {review.user.slice(0, 1)}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-foreground-900">
                            {review.user}
                          </p>
                          <p className="text-xs text-foreground-400">
                            {review.date}
                          </p>
                        </div>
                      </div>
                      <Stars value={review.rating} className="text-sm" />
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                      {review.content}
                    </p>
                    <p className="mt-2 flex items-center gap-1 text-xs text-foreground-400">
                      <i className="ri-thumb-up-line" />
                      {review.likes} 人觉得有用
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
                <span className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-chat-3-line text-2xl" />
                </span>
                <p className="text-sm text-foreground-600">
                  暂无该评分等级的点评内容
                </p>
                <p className="mt-1 text-xs text-foreground-400">
                  欢迎在右侧留下你的第一条点评
                </p>
              </div>
            )}
          </div>

          {/* Submit form */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <form
              onSubmit={handleSubmit}
              className="rounded-lg border border-background-200 bg-background-50 p-5"
            >
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className="ri-edit-2-line text-sm" />
                </span>
                发表点评
              </h2>

              <label className="mt-4 block">
                <span className="mb-1.5 block text-xs font-medium text-foreground-500">
                  昵称
                </span>
                <input
                  name="user"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="请输入你的昵称"
                  maxLength={20}
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </label>

              <div className="mt-4">
                <span className="mb-1.5 block text-xs font-medium text-foreground-500">
                  评分
                </span>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      aria-label={`评分 ${star} 星`}
                      onClick={() => setRating(star)}
                      className="flex h-8 w-8 cursor-pointer items-center justify-center text-xl text-accent-500"
                    >
                      <i
                        className={star <= rating ? "ri-star-fill" : "ri-star-line"}
                      />
                    </button>
                  ))}
                  <span className="ml-1 text-sm text-foreground-500">
                    {rating} 星
                  </span>
                </div>
              </div>

              <label className="mt-4 block">
                <span className="mb-1.5 block text-xs font-medium text-foreground-500">
                  点评内容
                </span>
                <textarea
                  name="content"
                  value={content}
                  onChange={(e) => setContent(e.target.value.slice(0, 500))}
                  rows={5}
                  maxLength={500}
                  placeholder="说说你对这部剧的看法..."
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
                <span className="mt-1 block text-right text-xs text-foreground-400">
                  {content.length} / 500
                </span>
              </label>

              {error && (
                <p className="mt-2 flex items-center gap-1.5 text-sm text-primary-700">
                  <i className="ri-error-warning-line" />
                  {error}
                </p>
              )}
              {feedback && (
                <p className="mt-2 flex items-center gap-1.5 text-sm text-accent-800">
                  <i className="ri-checkbox-circle-line" />
                  {feedback}
                </p>
              )}

              <button
                type="submit"
                className="mt-4 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                提交点评
              </button>
              <p className="mt-2 text-center text-xs text-foreground-400">
                当前为前端演示效果，点评仅保存在本地浏览器
              </p>
            </form>
          </aside>
        </div>
      </div>
    </div>
  );
}